import * as React from "react";
import Box, { BoxProps } from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";
import { X as XIcon } from "@phosphor-icons/react/X";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ResponsiveModalProps {
	/** Modal title - can be string or React node */
	title?: React.ReactNode;
	/** Modal content */
	children: React.ReactNode;
	/** Custom z-index for modal */
	zIndex?: number;
	/** Callback when close button is clicked */
	onClose?: () => void;
	/** Show close X button in top-right corner - defaults to true */
	showCloseButton?: boolean;
	/** Action buttons to display at bottom of modal */
	actions?: React.ReactNode;
	/** Where to position actions - defaults to 'content' */
	actionsPosition?: "content" | "footer";
	/** Event handlers (filtered for security) */
	[key: string]: unknown;
}

export interface ModalContentProps extends BoxProps {
	/** Content to display in modal body */
	children: React.ReactNode;
}

// ============================================================================
// RESPONSIVE LOGIC
// ============================================================================

function useResponsiveWidth() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md")); // <768px
	const isTablet = useMediaQuery(theme.breakpoints.between("md", "xl")); // 768px-1279px

	if (isMobile) {
		return "90vw"; // Mobile: 90% width
	}
	if (isTablet) {
		return "80vw"; // Tablet: 80% width
	}
	return "640px"; // Desktop: 640px width
}

// ============================================================================
// MODAL CONTENT COMPONENT
// ============================================================================

export function ModalContent(props: ModalContentProps): React.ReactElement {
	const { children, sx, ...other } = props;

	return (
		<Box
			sx={{
				padding: 3,
				...sx,
			}}
			{...other}>
			{children}
		</Box>
	);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function ResponsiveModalComponent(
	props: ResponsiveModalProps,
): React.ReactElement {
	const {
		title,
		children,
		zIndex = 1300,
		onClose,
		showCloseButton = true,
		actions,
		actionsPosition = "content",
		...eventProps
	} = props;

	const width = useResponsiveWidth();

	// Filter out event handlers to prevent React DOM warnings
	const filteredProps = Object.keys(eventProps).reduce(
		(acc, key) => {
			if (!key.startsWith("on")) {
				acc[key] = eventProps[key];
			}
			return acc;
		},
		{} as Record<string, unknown>,
	);

	return (
		<Box
			sx={{
				width,
				maxWidth: "640px",
				maxHeight: "calc(100vh - 48px)", // Always constrain height
				zIndex,
				display: "flex",
				flexDirection: "column",
			}}
			{...filteredProps}>
			<Paper
				sx={{
					border: "1px solid var(--mui-palette-divider)",
					boxShadow: "var(--mui-shadows-16)",
					p: 3,
					flex: 1,
					minHeight: 0, // Important for flex children
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}>
				{/* Header with title and optional close button */}
				{(title ?? (showCloseButton && onClose)) && (
					<Box
						sx={{
							position: "relative",
							mb: 3,
							flexShrink: 0,
						}}>
						{title && (
							<Typography
								variant="h5"
								sx={{
									pr: showCloseButton && onClose ? 5 : 0, // Make room for close button
								}}>
								{title}
							</Typography>
						)}
						{showCloseButton && onClose && (
							<IconButton
								onClick={onClose}
								size="small"
								sx={{
									position: "absolute",
									top: -8,
									right: -8,
									color: "text.secondary",
								}}>
								<XIcon size={20} />
							</IconButton>
						)}
					</Box>
				)}

				{/* Content area */}
				<Box
					sx={{
						flex: 1,
						overflow: "auto",
						minHeight: 0, // Important for overflow to work
						mb: actionsPosition === "content" && actions ? 3 : 0,
					}}>
					{children}
				</Box>

				{/* Actions - within content area */}
				{actionsPosition === "content" && actions && (
					<Box sx={{ flexShrink: 0 }}>{actions}</Box>
				)}
			</Paper>

			{/* Actions - footer position */}
			{actionsPosition === "footer" && actions && (
				<Box
					sx={{
						mt: 2,
						flexShrink: 0,
						px: 3,
						pb: 2,
					}}>
					{actions}
				</Box>
			)}
		</Box>
	);
}

// Export component directly for now - prop security causing issues
export const ResponsiveModal = ResponsiveModalComponent;
